import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptLocale from '@fullcalendar/core/locales/pt';
import { FaTimes, FaCalendarAlt, FaClipboardCheck } from 'react-icons/fa';
import EventAttendanceModal from '../Attendance/EventAttendanceModal';
import GameDetailModal from '../Game/GameDetailModal';
import { calculateSetScore } from '../../config/sportStats';
import './CalendarModal.css';
import API_BASE_URL from '../../config/api';

const CalendarModal = ({ isOpen, onClose, teamId, sportName, canManageAttendance = true }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
    const [selectedEventForAttendance, setSelectedEventForAttendance] = useState(null);
    const [gameResults, setGameResults] = useState({});
    const [isGameDetailOpen, setIsGameDetailOpen] = useState(false);
    const [selectedGameForDetail, setSelectedGameForDetail] = useState(null);

    useEffect(() => {
        if (isOpen && teamId) {
            fetchEvents();
        }
    }, [isOpen, teamId]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/events?teamId=${teamId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setEvents(data);

                const games = data.filter(e => e.eventType === 1);
                if (games.length > 0) {
                    const results = await Promise.allSettled(
                        games.map(g =>
                            fetch(`${API_BASE_URL}/api/game-results/${g.id}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            }).then(r => r.ok ? r.json() : null)
                        )
                    );
                    const map = {};
                    results.forEach((r, i) => {
                        if (r.status === 'fulfilled' && r.value?.result) {
                            map[games[i].id] = r.value.result;
                        }
                    });
                    setGameResults(map);
                }
            }
        } catch (error) {
            console.error('Error fetching calendar events:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatEventsForCalendar = () => {
        return events.map(event => ({
            id: event.id,
            title: event.title,
            start: event.startDateTime,
            end: event.endDateTime,
            backgroundColor: getEventColor(event.eventType),
            borderColor: getEventColor(event.eventType),
            extendedProps: {
                location: event.location,
                description: event.description,
                eventType: event.eventType
            }
        }));
    };

    const getEventColor = (eventType) => {
        switch (eventType) {
            case 1: return '#e74c3c'; // Game - Red
            case 2: return '#3498db'; // Training - Blue
            case 3: return '#2ecc71'; // Other - Green
            default: return '#95a5a6';
        }
    };

    const getEventTypeName = (eventType) => {
        switch (eventType) {
            case 1: return 'Jogo';
            case 2: return 'Treino';
            case 3: return 'Outro';
            default: return 'Evento';
        }
    };

    const handleDateClick = (info) => {
        setSelectedDate(info.date);
    };

    const handleBackToCalendar = () => {
        setSelectedDate(null);
    };

    const getEventsForSelectedDate = () => {
        if (!selectedDate) return [];
        return events.filter(event => {
            const eventDate = new Date(event.startDateTime);
            return eventDate.toDateString() === selectedDate.toDateString();
        });
    };

    const renderGameResult = (event) => {
        if (event.eventType !== 1) return null;
        const result = gameResults[event.id];
        if (!result) return null;

        let resultDisplay = null;

        if (result.setsJson) {
            try {
                const sets = JSON.parse(result.setsJson);
                const { homeSets, awaySets } = calculateSetScore(sets);
                resultDisplay = `${homeSets}–${awaySets}`;
            } catch { }
        } else if (result.homeScore != null && result.awayScore != null) {
            resultDisplay = `${result.homeScore}–${result.awayScore}`;
        }

        if (!resultDisplay) return null;

        return (
            <div className="card-row game-result">
                <div className="card-icon"><i className="fas fa-trophy"></i></div>
                <div className="card-text">
                    Resultado: {resultDisplay}
                    {result.isFinished ? '' : <span style={{ fontSize: '0.75rem', color: '#f59e0b', marginLeft: '6px', fontWeight: 'normal' }}>(Rascunho)</span>}
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="calendar-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="calendar-modal-content">
                <div className="calendar-modal-header">
                    <h2><FaCalendarAlt /> Calendário da Equipa</h2>
                    <button className="close-btn" onClick={onClose} aria-label="Fechar">
                        <FaTimes />
                    </button>
                </div>

                <div className="calendar-modal-body">
                    {loading ? (
                        <div className="calendar-loading">A carregar eventos…</div>
                    ) : selectedDate ? (
                        <div className="day-view-container">
                            <div className="day-view-header">
                                <button className="back-btn" onClick={handleBackToCalendar}>
                                    <i className="fas fa-arrow-left"></i> Voltar
                                </button>
                                <h3 className="day-view-title">
                                    {selectedDate.toLocaleDateString('pt-PT', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </h3>
                            </div>

                            <div className="events-list">
                                {getEventsForSelectedDate().length > 0 ? (
                                    getEventsForSelectedDate().map(event => (
                                        <div
                                            key={event.id}
                                            className="event-card"
                                            data-type={event.eventType}
                                        >
                                            <div className="event-card-header">
                                                <div style={{ flex: 1 }}>
                                                    <h4 className="event-card-title">{event.title}</h4>
                                                    <span
                                                        className="event-badge"
                                                        style={{
                                                            backgroundColor: getEventColor(event.eventType) + '15', // 15% opacity
                                                            color: getEventColor(event.eventType)
                                                        }}
                                                    >
                                                        {getEventTypeName(event.eventType)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="event-card-body">
                                                <div className="card-row">
                                                    <div className="card-icon"><i className="far fa-clock"></i></div>
                                                    <div className="card-text">
                                                        {new Date(event.startDateTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                                                        {' - '}
                                                        {new Date(event.endDateTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                                {event.location && (
                                                    <div className="card-row">
                                                        <div className="card-icon"><i className="fas fa-map-marker-alt"></i></div>
                                                        <div className="card-text">{event.location}</div>
                                                    </div>
                                                )}
                                                {renderGameResult(event)}
                                            </div>
                                            {event.eventType === 2 && canManageAttendance && (
                                                <div className="event-card-actions" style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                                                    <button
                                                        className="calendar-action-btn attendance"
                                                        onClick={() => {
                                                            setSelectedEventForAttendance(event);
                                                            setIsAttendanceModalOpen(true);
                                                        }}
                                                    >
                                                        <FaClipboardCheck /> Gerir Presenças
                                                    </button>
                                                </div>
                                            )}
                                            {event.eventType === 1 && (
                                                <div className="event-card-actions" style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                                                    <button
                                                        className="calendar-action-btn stats"
                                                        onClick={() => {
                                                            setSelectedGameForDetail(event);
                                                            setIsGameDetailOpen(true);
                                                        }}
                                                    >
                                                        <i className="fas fa-chart-bar"></i> Ver Estatísticas
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-events-message">
                                        Não existem eventos agendados para este dia.
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <FullCalendar
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{
                                left: 'prev,next today',
                                center: 'title',
                                right: 'dayGridMonth'
                            }}
                            events={formatEventsForCalendar()}
                            locale={ptLocale}
                            height="100%"
                            dayMaxEvents={4}
                            dateClick={handleDateClick}
                            buttonText={{
                                today: 'Hoje',
                                month: 'Mês',
                                week: 'Semana',
                                day: 'Dia',
                                list: 'Lista'
                            }}
                            eventClick={(info) => {
                                handleDateClick({ date: info.event.start });
                            }}
                            eventContent={(eventInfo) => {
                                const eventId = eventInfo.event.id;
                                const eventType = eventInfo.event.extendedProps.eventType;
                                const result = gameResults[eventId];

                                let resultDisplay = null;
                                if (eventType === 1 && result) {
                                    if (result.setsJson) {
                                        try {
                                            const sets = JSON.parse(result.setsJson);
                                            const { homeSets, awaySets } = calculateSetScore(sets);
                                            resultDisplay = `${homeSets}-${awaySets}`;
                                        } catch { }
                                    } else if (result.homeScore != null && result.awayScore != null) {
                                        resultDisplay = `${result.homeScore}-${result.awayScore}`;
                                    }
                                }

                                return (
                                    <div className="fc-event-main-frame" style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '2px',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        backgroundColor: eventInfo.event.backgroundColor,
                                        padding: '2px 4px',
                                        borderRadius: '4px'
                                    }}>
                                        <div className="fc-event-title-container" style={{ minWidth: 0, width: '100%' }}>
                                            <div className="fc-event-title fc-sticky" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                                {eventInfo.event.title}
                                            </div>
                                        </div>
                                        {resultDisplay && (
                                            <div style={{
                                                fontWeight: '900',
                                                color: '#fff',
                                                background: 'rgba(0,0,0,0.25)',
                                                padding: '1px 5px',
                                                borderRadius: '4px',
                                                fontSize: '0.65rem',
                                                width: 'fit-content',
                                                marginTop: '1px'
                                            }}>
                                                <i className="fas fa-trophy" style={{ fontSize: '0.6rem', marginRight: '3px' }}></i>
                                                {resultDisplay}
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    )}
                </div>
            </div>
            {selectedEventForAttendance && (
                <EventAttendanceModal
                    isOpen={isAttendanceModalOpen}
                    onClose={() => {
                        setIsAttendanceModalOpen(false);
                        setSelectedEventForAttendance(null);
                        fetchEvents(); // Refresh to show Any updates if they were showing on calendar
                    }}
                    event={selectedEventForAttendance}
                    teamId={teamId}
                />
            )}

            {selectedGameForDetail && (
                <GameDetailModal
                    isOpen={isGameDetailOpen}
                    onClose={() => {
                        setIsGameDetailOpen(false);
                        setSelectedGameForDetail(null);
                    }}
                    game={selectedGameForDetail}
                    sportName={sportName}
                    readOnly={true}
                />
            )}
        </div>
    );
};

export default CalendarModal;
